import { Box, Chip, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../Services/api.service";
import CustomButton from "../components/Button";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";

const PAYMENT_TERM_LABELS = {
  standard: "Standard 50 / 50",
  thirty_seventy: "30 / 70 before dispatch",
  full_advance: "100% advance",
  zero_cost_emi: "Zero Cost EMI",
};

const MyTasks = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [taskData, setTaskData] = useState({
    taskType: "",
    title: "",
    tasks: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const res = await apiService.fetchMyTasks(token);
      setTaskData(res);
    } catch (error) {
      toast.error("Error while fetching tasks");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAccountDone = async (orderId, taskKey) => {
    try {
      await apiService.toggleOrderAccountItem(orderId, taskKey, token);
      toast.success("Marked done");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleMarkInstalled = async (orderId) => {
    try {
      await apiService.markOrderInstalled(orderId, token);
      toast.success("Marked installed");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const renderRows = () => {
    if (taskData.taskType === "accounts") {
      return taskData.tasks.map((task, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "1.6fr 1.2fr 1.2fr auto",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography fontWeight={700}>{task.clinicName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {task.orderNumber}
            </Typography>
          </Box>
          <Typography variant="body2">{task.taskLabel}</Typography>
          <Typography variant="body2">
            {PAYMENT_TERM_LABELS[task.term] || task.term}
          </Typography>
          <CustomButton
            text="Mark done"
            backgroundColor="#fff"
            color="#000"
            hoverColor="#f0f0f0"
            width="110px"
            height="36px"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              textTransform: "none",
            }}
            onClick={() => handleMarkAccountDone(task.orderId, task.taskKey)}
          />
        </Box>
      ));
    }

    if (taskData.taskType === "installations") {
      return taskData.tasks.map((task, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "1.6fr 1.2fr 1fr auto",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
          }}
          onClick={() => navigate(`/dashboard/orders/${task.orderId}`)}
        >
          <Box>
            <Typography fontWeight={700}>{task.clinicName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {task.orderNumber}
            </Typography>
          </Box>
          <Typography variant="body2">{task.technicianName}</Typography>
          <Chip
            label={task.status === "scheduled" ? "SCHEDULED" : "PENDING"}
            size="small"
            color={task.status === "scheduled" ? "warning" : "default"}
          />
          {task.status === "scheduled" && (
            <CustomButton
              text="Mark installed"
              width="140px"
              height="36px"
              sx={{ textTransform: "none" }}
              onClick={(e) => {
                e.stopPropagation();
                handleMarkInstalled(task.orderId);
              }}
            />
          )}
        </Box>
      ));
    }

    if (taskData.taskType === "dispatch") {
      return taskData.tasks.map((task, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "1.6fr 1.2fr auto",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
          }}
          onClick={() => navigate(`/dashboard/orders/${task.orderId}`)}
        >
          <Box>
            <Typography fontWeight={700}>{task.clinicName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {task.orderNumber}
            </Typography>
          </Box>
          <Typography variant="body2">
            {PAYMENT_TERM_LABELS[task.term] || task.term}
          </Typography>
          <Typography variant="body2" color="primary">
            Add tracking ›
          </Typography>
        </Box>
      ));
    }

    if (taskData.taskType === "awaiting_customer") {
      return taskData.tasks.map((task, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "1.6fr 1.2fr auto",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
          }}
          onClick={() => navigate(`/dashboard/orders/${task.orderId}`)}
        >
          <Box>
            <Typography fontWeight={700}>{task.clinicName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {task.orderNumber}
            </Typography>
          </Box>
          <Typography variant="body2">{task.document}</Typography>
          <Chip label={task.status} size="small" color="warning" />
        </Box>
      ));
    }

    if (taskData.taskType === "combined") {
      return taskData.tasks.map((task, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr 1.4fr 1fr",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
          }}
          onClick={() => navigate(`/dashboard/orders/${task.orderId}`)}
        >
          <Box>
            <Typography fontWeight={700}>{task.clinicName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {task.orderNumber}
            </Typography>
          </Box>
          <Chip label={task.category} size="small" variant="outlined" />
          <Typography variant="body2">
            {task.taskLabel || task.document || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {task.status || ""}
          </Typography>
        </Box>
      ));
    }

    return null;
  };

  const columnLabels = {
    accounts: ["Clinic", "Task", "Term", ""],
    installations: ["Clinic", "Technician", "Status", ""],
    dispatch: ["Clinic", "Term", ""],
    awaiting_customer: ["Clinic", "Document", "Status"],
    combined: ["Clinic", "Category", "Task", "Status"],
  };

  const columns = columnLabels[taskData.taskType] || [];
  const gridTemplate =
    taskData.taskType === "accounts"
      ? "1.6fr 1.2fr 1.2fr auto"
      : taskData.taskType === "installations"
        ? "1.6fr 1.2fr 1fr auto"
        : taskData.taskType === "combined"
          ? "1.4fr 1fr 1.4fr 1fr"
          : "1.6fr 1.2fr auto";

  return (
    <Box sx={{ padding: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          My tasks
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Auto-routed to you based on order stage.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {taskData.title || "Tasks"}
          </Typography>
        </Box>

        {columns.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: gridTemplate,
              px: 2.5,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            {columns.map((c) => (
              <Typography
                key={c}
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                {c.toUpperCase()}
              </Typography>
            ))}
          </Box>
        )}

        {!isLoading && taskData.tasks.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              No tasks right now. 🎉
            </Typography>
          </Box>
        )}

        {renderRows()}
      </Paper>

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default MyTasks;
